import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {
  ContentCopy,
  Download,
  GppGood,
  GppMaybe,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { toast } from "react-toastify";

import OtpInput from "../components/OtpInput";
import { get, post } from "../services/apiMethods";
import URLS from "../services/urlConstants";

const MuiBox = styled(Box)({
  width: "100%",
  display: "flex",
  justifyContent: "center",
  alignContent: "center",
  alignItems: "center",
  alignSelf: "center",
});

const CodeChip = styled(Box)(({ theme }) => ({
  fontFamily: "monospace",
  fontSize: "15px",
  letterSpacing: "1px",
  padding: "8px 10px",
  borderRadius: theme.shape.borderRadius,
  border: "1px solid",
  borderColor: theme.palette.divider,
  textAlign: "center",
}));

export default function TwoFactorAuth() {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);

  /* Enrollment */
  const [setupData, setSetupData] = useState(null);
  const [startingSetup, setStartingSetup] = useState(false);
  const [setupOtp, setSetupOtp] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  /* Backup codes shown once, right after they are issued */
  const [newBackupCodes, setNewBackupCodes] = useState(null);

  /* Disable dialog */
  const [disableOpen, setDisableOpen] = useState(false);
  const [disablePassword, setDisablePassword] = useState("");
  const [disableOtp, setDisableOtp] = useState("");
  const [disabling, setDisabling] = useState(false);

  /* Regenerate dialog */
  const [regenerateOpen, setRegenerateOpen] = useState(false);
  const [regenerateOtp, setRegenerateOtp] = useState("");
  const [regenerating, setRegenerating] = useState(false);

  const fetchStatus = async () => {
    try {
      const { data } = await get(URLS.auth.twoFactorStatus);
      setStatus(data.result);
    } catch (error) {
      console.log(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleStartSetup = async () => {
    try {
      setStartingSetup(true);
      const { data } = await post(URLS.auth.setupTwoFactor, {});
      setSetupData(data.result);
      setSetupOtp("");
      setShowSecret(false);
    } catch (error) {
      console.log(error);
    }
    setStartingSetup(false);
  };

  const handleConfirmSetup = async (codeOverride) => {
    const token = codeOverride || setupOtp;
    if (token.length !== 6) return;

    try {
      setConfirming(true);
      const { data } = await post(URLS.auth.verifyTwoFactorSetup, { token });
      setNewBackupCodes(data.result.backupCodes);
      setSetupData(null);
      setSetupOtp("");
      await fetchStatus();
    } catch (error) {
      console.log(error);
      setSetupOtp("");
    }
    setConfirming(false);
  };

  const handleDisable = async () => {
    try {
      setDisabling(true);
      await post(URLS.auth.disableTwoFactor, {
        password: disablePassword,
        token: disableOtp,
      });
      setDisableOpen(false);
      setDisablePassword("");
      setDisableOtp("");
      await fetchStatus();
    } catch (error) {
      console.log(error);
      setDisableOtp("");
    }
    setDisabling(false);
  };

  const handleRegenerate = async () => {
    try {
      setRegenerating(true);
      const { data } = await post(URLS.auth.regenerateBackupCodes, {
        token: regenerateOtp,
      });
      setRegenerateOpen(false);
      setRegenerateOtp("");
      setNewBackupCodes(data.result.backupCodes);
      await fetchStatus();
    } catch (error) {
      console.log(error);
      setRegenerateOtp("");
    }
    setRegenerating(false);
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch (error) {
      toast.error("Unable to copy");
    }
  };

  const downloadBackupCodes = () => {
    const content = [
      "Inventory - two-step verification backup codes",
      `Generated: ${new Date().toLocaleString()}`,
      "",
      "Each code can be used once if you lose access to your authenticator app.",
      "",
      ...newBackupCodes,
      "",
    ].join("\n");

    const url = URL.createObjectURL(
      new Blob([content], { type: "text/plain;charset=utf-8" })
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = "inventory-backup-codes.txt";
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <MuiBox sx={{ py: 6 }}>
        <CircularProgress size={28} />
      </MuiBox>
    );
  }

  return (
    <MuiBox>
      <Paper sx={{ width: "100%", p: 3 }}>
        <Typography
          sx={{
            fontSize: "25px",
            fontWeight: 700,
            textAlign: "center",
            mb: 1,
          }}
        >
          Two-Step Verification
        </Typography>

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="center"
          sx={{ mb: 3 }}
        >
          <Chip
            size="small"
            color={status?.enabled ? "success" : "default"}
            icon={
              status?.enabled ? (
                <GppGood fontSize="small" />
              ) : (
                <GppMaybe fontSize="small" />
              )
            }
            label={status?.enabled ? "Enabled" : "Not enabled"}
          />
          {status?.enabled && (
            <Chip
              size="small"
              variant="outlined"
              label={`${status.backupCodesRemaining} backup code${
                status.backupCodesRemaining === 1 ? "" : "s"
              } left`}
            />
          )}
        </Stack>

        {/* ---------------- Enabled ---------------- */}
        {status?.enabled && (
          <>
            <Alert severity="success" sx={{ mb: 2 }}>
              Your account is protected. You will be asked for a code from your
              authenticator app every time you sign in.
            </Alert>

            {status.backupCodesRemaining === 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                You have no backup codes left. Generate a new set so you can
                still sign in if you lose your phone.
              </Alert>
            )}

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ mt: 3 }}
            >
              <Button
                variant="outlined"
                fullWidth
                onClick={() => setRegenerateOpen(true)}
              >
                Generate new backup codes
              </Button>
              <Button
                variant="outlined"
                color="error"
                fullWidth
                onClick={() => setDisableOpen(true)}
              >
                Turn off
              </Button>
            </Stack>
          </>
        )}

        {/* ---------------- Not enabled, setup not started ---------------- */}
        {!status?.enabled && !setupData && (
          <>
            <Typography sx={{ mb: 2, color: "text.secondary" }}>
              Add a second step to your sign-in. After entering your password
              you will be asked for a 6-digit code from an authenticator app
              such as Google Authenticator, Microsoft Authenticator or Authy.
            </Typography>

            <LoadingButton
              loading={startingSetup}
              variant="contained"
              fullWidth
              sx={{ height: 45, mt: 1 }}
              onClick={handleStartSetup}
            >
              Set up authenticator app
            </LoadingButton>
          </>
        )}

        {/* ---------------- Enrollment in progress ---------------- */}
        {!status?.enabled && setupData && (
          <>
            <Divider sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: "13px", color: "text.secondary" }}>
                Step 1 - Scan
              </Typography>
            </Divider>

            <Typography sx={{ mb: 2, color: "text.secondary" }}>
              Open your authenticator app and scan this QR code.
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
              <Box
                component="img"
                src={setupData.qrCode}
                alt="Authenticator QR code"
                sx={{
                  width: 200,
                  height: 200,
                  p: 1,
                  backgroundColor: "#fff",
                  borderRadius: 1,
                }}
              />
            </Box>

            <Typography
              sx={{ fontSize: "13px", color: "text.secondary", mb: 1 }}
              textAlign="center"
            >
              Can't scan? Enter this key manually:
            </Typography>

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="center"
              sx={{ mb: 3 }}
            >
              <CodeChip>
                {showSecret
                  ? setupData.secret
                  : "•".repeat(setupData.secret.length)}
              </CodeChip>
              <Tooltip title={showSecret ? "Hide" : "Show"}>
                <IconButton
                  size="small"
                  onClick={() => setShowSecret((prev) => !prev)}
                >
                  {showSecret ? (
                    <VisibilityOff fontSize="small" />
                  ) : (
                    <Visibility fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
              <Tooltip title="Copy key">
                <IconButton
                  size="small"
                  onClick={() => copyToClipboard(setupData.secret, "Setup key")}
                >
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>

            <Divider sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: "13px", color: "text.secondary" }}>
                Step 2 - Verify
              </Typography>
            </Divider>

            <Typography
              sx={{ mb: 2, color: "text.secondary" }}
              textAlign="center"
            >
              Enter the 6-digit code shown in your app to finish.
            </Typography>

            <OtpInput
              value={setupOtp}
              onChange={setSetupOtp}
              onComplete={(code) => handleConfirmSetup(code)}
              disabled={confirming}
            />

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ mt: 3 }}
            >
              <Button
                variant="outlined"
                fullWidth
                disabled={confirming}
                onClick={() => {
                  setSetupData(null);
                  setSetupOtp("");
                }}
              >
                Cancel
              </Button>
              <LoadingButton
                loading={confirming}
                variant="contained"
                fullWidth
                disabled={setupOtp.length !== 6}
                onClick={() => handleConfirmSetup()}
              >
                Verify and enable
              </LoadingButton>
            </Stack>
          </>
        )}
      </Paper>

      {/* ---------------- Backup codes, shown once ---------------- */}
      <Dialog
        open={!!newBackupCodes}
        maxWidth="xs"
        fullWidth
        onClose={() => setNewBackupCodes(null)}
      >
        <DialogTitle>Save your backup codes</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            These codes are shown only once. Store them somewhere safe - each
            one lets you sign in a single time if you lose your phone.
          </Alert>
          <Grid container spacing={1}>
            {(newBackupCodes || []).map((code) => (
              <Grid item xs={6} key={code}>
                <CodeChip>{code}</CodeChip>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            size="small"
            startIcon={<ContentCopy fontSize="small" />}
            onClick={() =>
              copyToClipboard((newBackupCodes || []).join("\n"), "Backup codes")
            }
          >
            Copy
          </Button>
          <Button
            size="small"
            startIcon={<Download fontSize="small" />}
            onClick={downloadBackupCodes}
          >
            Download
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => setNewBackupCodes(null)}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------------- Turn off ---------------- */}
      <Dialog
        open={disableOpen}
        maxWidth="xs"
        fullWidth
        onClose={() => setDisableOpen(false)}
      >
        <DialogTitle>Turn off two-step verification</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "14px", mb: 2 }}>
            Confirm with your password and a current code from your
            authenticator app.
          </DialogContentText>
          <TextField
            fullWidth
            type="password"
            label="Password"
            value={disablePassword}
            onChange={(e) => setDisablePassword(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ autoComplete: "current-password" }}
            sx={{ mb: 3 }}
          />
          <OtpInput
            value={disableOtp}
            onChange={setDisableOtp}
            disabled={disabling}
            autoFocus={false}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button size="small" onClick={() => setDisableOpen(false)}>
            Cancel
          </Button>
          <LoadingButton
            size="small"
            loading={disabling}
            color="error"
            variant="contained"
            disabled={!disablePassword || disableOtp.length !== 6}
            onClick={handleDisable}
          >
            Turn off
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* ---------------- Regenerate backup codes ---------------- */}
      <Dialog
        open={regenerateOpen}
        maxWidth="xs"
        fullWidth
        onClose={() => setRegenerateOpen(false)}
      >
        <DialogTitle>Generate new backup codes</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "14px", mb: 2 }}>
            Your existing backup codes will stop working. Enter a current code
            from your authenticator app to continue.
          </DialogContentText>
          <OtpInput
            value={regenerateOtp}
            onChange={setRegenerateOtp}
            disabled={regenerating}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button size="small" onClick={() => setRegenerateOpen(false)}>
            Cancel
          </Button>
          <LoadingButton
            size="small"
            loading={regenerating}
            variant="contained"
            disabled={regenerateOtp.length !== 6}
            onClick={handleRegenerate}
          >
            Generate
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </MuiBox>
  );
}
